/* inviteSheetComponent.jsx */
import React, { useState, useRef, useLayoutEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './inviteprint.css';

/* ===============================================================
   定数定義
   =============================================================== */

/** 日本語の曜日配列 */
const JWD = ['日', '月', '火', '水', '木', '金', '土'];

/* ===============================================================
   ヘルパー関数（日付・時刻フォーマット）
   =============================================================== */

/** 数値を2桁にゼロ埋め */
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Dateオブジェクトから 'YYYY/MM/DD' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列
 */
const fmtYMD = (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;

/**
 * Dateオブジェクトから 'YYYY/MM/DD(曜)' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列（曜日付き）
 */
const fmtYMDw = (d) => `${fmtYMD(d)}(${JWD[d.getDay()]})`;

/**
 * Dateオブジェクトから 'HH：MM' 形式の文字列を生成（全角コロン）
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた時刻文字列
 */
const fmtHMJP = (d) => `${pad2(d.getHours())}：${pad2(d.getMinutes())}`;

/* ===============================================================
   ヘルパー関数（日時変換・パース）
   =============================================================== */

/**
 * "HH:MM:SS" → "HH：MM" に変換（秒は捨てる）
 * @param {string} tStr - "HH:MM:SS" 形式などの時刻文字列
 * @returns {string} - "HH：MM" 形式（全角コロン）
 */
const formatReceptionTime = (tStr) => {
  if (!tStr) return '';
  const parts = String(tStr).split(':');
  if (parts.length < 2) return tStr;
  const h = parts[0].padStart(2, '0');
  const m = parts[1].padStart(2, '0');
  return `${h}：${m}`;
};

/* ===============================================================
   ヘルパー関数（開催期間の描画）
   =============================================================== */

/**
 * イベントの開催期間（日付・時刻・受付時間）をレンダリング
 * @param {Object} event - イベント情報
 * @returns {React.ReactNode}
 */
const renderSchedulePeriod = (event) => {
  const schedules = event?.schedules || [];
  if (schedules.length === 0) return null;

  const fmt = (t) => {
    const d = new Date(`1970-01-01T${String(t).slice(0, 5)}:00`);
    return isNaN(d) ? '' : fmtHMJP(d);
  };

  const times_unified = event?.times_unified;
  const receptUnified = schedules.every(s =>
    s.recept_start_time === schedules[0].recept_start_time &&
    s.recept_end_time === schedules[0].recept_end_time
  );
  const hasRecept = schedules.some(s => s.recept_start_time);

  if (times_unified) {
    const first = schedules[0];
    const last  = schedules[schedules.length - 1];
    const datePart = schedules.length === 1
      ? fmtYMDw(new Date(first.date))
      : `${fmtYMDw(new Date(first.date))}～${fmtYMDw(new Date(last.date))}`;
    return (
      <>
        <div className="ip-sumDateLine">{datePart}</div>
        <div className="ip-sumTimeLine">{fmt(first.start_time)}～{fmt(first.end_time)}</div>
        {hasRecept && first.recept_start_time && (
          <div className="ip-sumReceptionLine">
            受付時間：{formatReceptionTime(first.recept_start_time)}～{formatReceptionTime(first.recept_end_time)}
          </div>
        )}
      </>
    );
  }

  if (receptUnified) {
    const first = schedules[0];
    return (
      <>
        {schedules.map((s, i) => (
          <div key={i} className="ip-sumDateLine">
            {fmtYMDw(new Date(s.date))}　{fmt(s.start_time)}～{fmt(s.end_time)}
          </div>
        ))}
        {hasRecept && first.recept_start_time && (
          <div className="ip-sumReceptionLine">
            受付時間：{formatReceptionTime(first.recept_start_time)}～{formatReceptionTime(first.recept_end_time)}
          </div>
        )}
      </>
    );
  }

  return schedules.map((s, i) => (
    <div key={i}>
      <div className="ip-sumDateLine">
        {fmtYMDw(new Date(s.date))}　{fmt(s.start_time)}～{fmt(s.end_time)}
      </div>
      {s.recept_start_time && (
        <div className="ip-sumReceptionLine">
          受付：{formatReceptionTime(s.recept_start_time)}～{formatReceptionTime(s.recept_end_time)}
        </div>
      )}
    </div>
  ));
};

/* ===============================================================
   サブコンポーネント（FitLineFixed）
   =============================================================== */

/**
 * テキストを指定幅に収まるようフォントサイズを自動調整するコンポーネント
 * @param {Object} props - { children, className, targetWidth, maxFont, minFont, step, margin, onMeasured }
 */
export function FitLineFixed({
  children,
  className,
  targetWidth = 360,
  maxFont = 30,
  minFont = 12,
  step = 0.5,        // 刻み幅（0.5px単位で調整可能に）
  margin = 1,        // 安全マージン（px）
  onMeasured,
}) {
  const ref = useRef(null);
  const [fontSize, setFontSize] = useState(maxFont);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    Object.assign(el.style, {
      whiteSpace: "pre",
      visibility: "hidden",
      fontSize: maxFont + "px",
    });

    const measure = () => {
      const parent = el.parentElement;
      const available = targetWidth || parent?.clientWidth || 0;
      if (!available) {
        requestAnimationFrame(measure);
        return;
      }

      // 安全マージンを引いた有効幅
      const effectiveWidth = available - margin;

      // 小数対応の二分探索
      let lo = minFont, hi = maxFont, best = minFont;
      while (hi - lo > step / 2) {
        const mid2 = (lo + hi) / 2;
        const midRounded = Math.round(mid2 / step) * step;

        el.style.fontSize = midRounded + "px";
        void el.offsetWidth;

        if (el.scrollWidth <= effectiveWidth) {
          best = midRounded;
          lo = midRounded + step;
        } else {
          hi = midRounded - step;
        }
      }

      // 最終検証：本当に収まるか確認
      el.style.fontSize = best + "px";
      void el.offsetWidth;
      if (el.scrollWidth > effectiveWidth && best > minFont) {
        best = Math.max(minFont, best - step);
      }

      setFontSize(best);
      onMeasured?.(best);
      el.style.visibility = "";
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const fontsReady = (document.fonts?.ready ?? Promise.resolve()).then(measure);
    const ro = new ResizeObserver(() => measure());
    if (el.parentElement) ro.observe(el.parentElement);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [children, targetWidth, maxFont, minFont, step, margin, onMeasured]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        maxWidth: targetWidth,
        width: "100%",
        overflow: "hidden",
        whiteSpace: "pre",
        fontSize,
      }}
    >
      {children}
    </div>
  );
}

/* ===============================================================
   招待状シートコンポーネント
   =============================================================== */

/**
 * 招待状シートを表示するコンポーネント
 * @param {Object} props
 * @param {Object} props.event - イベント情報
 * @param {Object} props.participant - 参加者情報
 * @param {string} props.qrCode - QRコードの値
 * @param {React.Ref} props.sheetRef - シートへのref（PDF生成用）
 */
export function InviteSheet({ event, participant, qrCode, sheetRef }) {
  if (!event || !participant) return null;

  /* ---------------------------------------------------------------
     名札セクション（左上：QRあり）
     --------------------------------------------------------------- */
  const renderBadgeSection = () => {
    const badgeTag = participant.badge_tag ?? '';
    const badgeColor = participant.badge_tag_color || '#17823b';

    const lines1 = participant.detail_lines1 ?? [];
    const lines2 = participant.detail_lines2 ?? [];
    const lines3 = participant.detail_lines3 ?? [];
    const lines4 = participant.detail_lines4 ?? [];

    return (
      <div className="ip-quad ip-badge">

        <div className="ip-badgeDetail">
          {[...lines1, ...lines2, '\u00A0', '\u00A0'].slice(0, 2).map((line, i) => (
            <FitLineFixed className="extra" key={`dl-${i}`}>
              {line}
            </FitLineFixed>
          ))}

          {[...lines3, ...lines4, '\u00A0', '\u00A0'].slice(0, 2).map((line, i) => (
            <FitLineFixed className="extra" key={`dl-small-${i}`} maxFont={23} minFont={8}>
              {line}
            </FitLineFixed>
          ))}
          <FitLineFixed className="person">
            {participant.participant_name}　様
          </FitLineFixed>
        </div>

        <div className="ip-badgeQr fancy">
          <QRCodeSVG value={qrCode} size={96} />
        </div>

        <div className="ip-badgeTag" style={{ '--badge-color': badgeColor }}>
          <FitLineFixed>{badgeTag || '\u00A0'}</FitLineFixed>
        </div>

        <div className="ip-badgeDetail">
          <FitLineFixed className="person">
            {participant.host_name}
          </FitLineFixed>
        </div>

      </div>
    );
  };

  /* ---------------------------------------------------------------
     名札セクション（右上：QRなし）
     --------------------------------------------------------------- */
  const renderBadgeSectionNoQr = () => {
    const badgeTag = participant.badge_tag ?? '';
    const badgeColor = participant.badge_tag_color || '#17823b';
    const lines1 = participant.detail_lines1 ?? [];
    const lines2 = participant.detail_lines2 ?? [];
    const lines3 = participant.detail_lines3 ?? [];
    const lines4 = participant.detail_lines4 ?? [];

    return (
      <div className="ip-quad ip-badge-noqr ip-badge">
        <div className="ip-badgeDetail">
          {[...lines1, ...lines2, '\u00A0', '\u00A0'].slice(0, 2).map((line, i) => (
            <FitLineFixed className="extra" key={`dl-${i}`}>
              {line}
            </FitLineFixed>
          ))}

          {[...lines3, ...lines4, '\u00A0', '\u00A0'].slice(0, 2).map((line, i) => (
            <FitLineFixed className="extra" key={`dl-small-${i}`} maxFont={23} minFont={8}>
              {line}
            </FitLineFixed>
          ))}
          <FitLineFixed className="person">
            {participant.participant_name}　様
          </FitLineFixed>
        </div>

        <div className="ip-badgeQr fancy">
          <QRCodeSVG value={qrCode} size={96} />
        </div>

        <div className="ip-badgeTag" style={{ '--badge-color': badgeColor }}>
          <FitLineFixed>{badgeTag || '\u00A0'}</FitLineFixed>
        </div>

        <div className="ip-badgeDetail">
          <FitLineFixed className="person">
            {participant.host_name}
          </FitLineFixed>
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------
     サマリーセクション（左下）
     --------------------------------------------------------------- */
  const renderSummarySection = () => (
    <div className="ip-quad ip-summary ip-summaryCard">
      {/* ヘッダー */}
      <div className="ip-sumHeader">
        <div className="ip-sumHeaderLine1">
          {event.schedules?.[0]?.date
            ? new Date(event.schedules[0].date).getFullYear() + '年度'
            : ''}
        </div>
        <FitLineFixed className="ip-sumHeaderLine2">
          {event.event_title}
        </FitLineFixed>
        <div className="ip-sumHeaderLine2">展示会入場証</div>
      </div>

      {/* 開催期間 */}
      <div className="ip-sumSection">
        <div className="ip-sumSectionTitle">開催期間</div>
        <div className="ip-sumSectionBody">
          {renderSchedulePeriod(event)}
        </div>
      </div>

      {/* 会場 */}
      <div className="ip-sumSection">
        <div className="ip-sumSectionTitle">会場</div>
        <div className="ip-sumSectionBody">
          <div className="ip-location-text">{event?.event_location || '-'}</div>
          <div className="ip-address-text">{event?.event_address || ""}</div>
        </div>
      </div>

      {/* 運営事務局 */}
      <div className="ip-sumSection">
        <div className="ip-sumSectionTitle">運営事務局</div>
        <div className="ip-sumSectionBody">
          <div className="ip-office-text">{event?.management_office || ""}</div>
        </div>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------
     注意事項セクション（右下）
     --------------------------------------------------------------- */
  const renderNotesSection = () => (
    <div className="ip-quad ip-notes">
      <h4>＜ご案内・注意事項＞</h4>

      {/* 注意書き */}
      <p className="ip-sumNotice">
        ※このページをA4サイズのカラーで印刷し<br />
        当日、必ず会場へご持参ください。
      </p>

      <div className="ip-notes-text">{event?.information_notes || ""}</div>
    </div>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="ip-sheet" ref={sheetRef}>
      {renderBadgeSection()}       {/* 左上：名札（QRあり） */}
      {renderBadgeSectionNoQr()}   {/* 右上：名札（QRなし） */}
      {renderSummarySection()}     {/* 左下：サマリー */}
      {renderNotesSection()}       {/* 右下：注意事項 */}
    </div>
  );
}

export default InviteSheet;